/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useEffect, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { useAINudges } from '@atlassian/ai-smart-button-api';
import { useConversationActionsStoreActions } from '@atlassian/rovo-conversation-actions/conversation-actions-store';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';
import { isChatAction } from '@atlassian/rovo-conversation-actions/utils';

import { useAISmartButtonContext } from '../../../../common/context';
import { isBrowser } from '../../../../common/utils/is-browser';
import { isNudgeRecommendationSurface } from '../../../../common/utils/is-nudge-recommendation-surface';
import { CONVO_STARTER_ID } from '../../../../constants';
import { useFetchNudgeRecommendations } from '../../../../hooks/useFetchNudgeRecommendations';
import { findActiveNudge } from '../../../../utils/findActiveNudge';

import { useNudgeSessionState } from './use-nudge-session-state';

const messages = defineMessages({
	convoStarterSubheader: {
		id: 'ai-smart-button.convo-starter.nudge.subheader',
		defaultMessage: 'Open Rovo chat with this page-specific starter',
		description: 'Subheader text shown on hover for conversation starter nudges',
	},
});

// Lazy load to avoid loading useRovoActionTrigger when there's no active nudge
const LazyAIProactiveNudge = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_ai-proactive-nudge" */ './main').then(
		(module) => ({
			default: module.AIProactiveNudge,
		}),
	),
);

type AIProactiveNudgeProps = {
	onActiveNudgeChange?: (hasActiveNudge: boolean) => void;
};

export const AIProactiveNudge = ({
	onActiveNudgeChange,
}: AIProactiveNudgeProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { product, pageMode, isLiveViewMode } = useAISmartButtonContext();
	const isLiveDoc = pageMode === 'live';
	const [triggerId] = useState(() => crypto.randomUUID());

	const selectedNudges = useAINudges();
	const shouldTrigger = Boolean(selectedNudges?.length);
	const hasPageCatchupNudge =
		selectedNudges?.some((nudge) => nudge.nudgeContext?.id === 'content-catchup') ?? false;
	const pageUrl = isBrowser() ? `${window.location.origin}${window.location.pathname}` : '';
	const { recommendations, loading, error } = useFetchNudgeRecommendations(
		triggerId,
		shouldTrigger,
		pageUrl,
		product,
		hasPageCatchupNudge,
		isLiveDoc,
		isLiveViewMode,
	);
	const { acceptedNudges, dismissedNudges, addAcceptedNudge, addDismissedNudge } =
		useNudgeSessionState();
	const { setConvoStarterActionPrompt } = useConversationActionsStoreActions();

	const activeNudge: ConversationAction | null = useMemo(() => {
		// TODO: Remove when new consumers can safely use recommendations without exceeding request rate limits
		// Temporary: added search to skip nudge BE
		if (!isNudgeRecommendationSurface({ product, pageUrl, isLiveDoc, isLiveViewMode })) {
			return (
				selectedNudges.filter(
					(nudge) => !dismissedNudges.includes(nudge.id) && !acceptedNudges.includes(nudge.id),
				)[0] ?? null
			);
		}

		// Self-eligible nudges bypass the loading guard
		const availableNudges = selectedNudges.filter(
			(nudge) => !dismissedNudges.includes(nudge.id) && !acceptedNudges.includes(nudge.id),
		);
		const selfEligibleNudge = availableNudges.find((nudge) => nudge.nudgeContext?.selfEligible);
		if (selfEligibleNudge) {
			return selfEligibleNudge;
		}

		if (loading || error || !recommendations?.length || !selectedNudges?.length) {
			return null;
		}
		// TODO: dismiss/accept nudge by its nudgeContext.id so we only need to check recommendations
		// Remove conversation starter if dismissed or accepted
		const availableRecommendations = recommendations.filter(
			(recommendation) =>
				!dismissedNudges.includes(recommendation.feature_details.feature_id) &&
				!acceptedNudges.includes(recommendation.feature_details.feature_id),
		);
		const nudge = findActiveNudge(availableRecommendations, availableNudges) ?? null;
		if (nudge?.id === CONVO_STARTER_ID) {
			return {
				...nudge,
				secondaryTitle: formatMessage(messages.convoStarterSubheader),
			};
		}
		return nudge;
	}, [
		acceptedNudges,
		dismissedNudges,
		error,
		loading,
		product,
		recommendations,
		selectedNudges,
		formatMessage,
		pageUrl,
		isLiveDoc,
		isLiveViewMode,
	]);

	// Notify parent about active nudge state
	useEffect(() => {
		onActiveNudgeChange?.(Boolean(activeNudge));
	}, [activeNudge, onActiveNudgeChange]);

	// Sync the active nudge convo starter prompt into the shared store so the actions panel
	// can find the matching starter and pin it at position 0.
	useEffect(() => {
		if (
			activeNudge?.id === CONVO_STARTER_ID &&
			isChatAction(activeNudge) &&
			fg('aix_convo_starter_nudge_enabled')
		) {
			setConvoStarterActionPrompt(activeNudge.staticPrompt);
		} else {
			setConvoStarterActionPrompt(undefined);
		}
		return () => {
			setConvoStarterActionPrompt(undefined);
		};
	}, [activeNudge, setConvoStarterActionPrompt]);

	// Early exit if no nudge to show
	if (!activeNudge) {
		return null;
	}

	return (
		<React.Suspense fallback={null}>
			<LazyAIProactiveNudge
				activeNudge={activeNudge}
				addAcceptedNudge={addAcceptedNudge}
				addDismissedNudge={addDismissedNudge}
			/>
		</React.Suspense>
	);
};
